/**
 * Dev-tools gate — "show these controls to ME, on any deploy including main."
 *
 * The variant switcher and the location panel used to hang off
 * `REACT_APP_BRANCH !== 'main'`, which answers the wrong question. It asks
 * WHERE the app is running when what we need to know is WHO is looking: branch
 * deploys are exactly where device tests with other people happen, so the
 * branch gate showed the pills to testers, and it hid them on main where the
 * real variant comparison now has to happen (user, 2026-08-20).
 *
 * A localStorage flag, set by a URL, rather than an identity check:
 *
 *   - No user id in the bundle. Hard-coding a prod `profile_user_id` to mean
 *     "Mark" ships a real person's key in public JS.
 *   - Test personas are the normal case, not the exception (CLAUDE-PROJECT:
 *     test rows are structurally indistinguishable BY DESIGN). An identity gate
 *     goes dark on every fresh probe account, which is most of the testing.
 *   - No schema change — prod migrations are a discussed-in-detail event, and
 *     a debug pill does not earn one.
 *   - Per-origin is precisely the semantics we want: enable once on prod, and
 *     a tester's device simply never has the key.
 *
 * NOT a security boundary, and it must never become one. It hides clutter from
 * people who would find it confusing; it does not protect anything. Everything
 * behind it is a client-side view choice the person could make from the console
 * anyway.
 *
 * Enable:  royale.app/?dev=1     Disable: royale.app/?dev=0
 */

const DEV_TOOLS_KEY = 'royale_dev_tools';

/** Is the person looking at this device someone who wants the debug chrome? */
export const isDevToolsEnabled = () => {
  try {
    return localStorage.getItem(DEV_TOOLS_KEY) === 'true';
  } catch (e) {
    // Private-mode Safari throws on localStorage. No flag = no pills, which is
    // the safe direction to fail.
    return false;
  }
};

/** Branch deploys only — the location diagnostic stays off prod entirely
 *  (user, 2026-08-20: "I don't need LOC on main, just dev"). */
export const isBranchDeploy = () => process.env.REACT_APP_BRANCH !== 'main';

/**
 * Consume `?dev=1` / `?dev=0` and strip it from the URL.
 *
 * Stripping matters for more than tidiness: this app's permanent room links get
 * copied, texted and turned into QR codes, so a `?dev=` left on the address bar
 * would ride along and silently switch the flag on for whoever opened it.
 * history.replaceState keeps the entry rather than adding one, so Back still
 * goes where the person expects.
 *
 * Returns true if the flag changed, for the caller's logging. Safe to call on
 * every load; a no-op without the param.
 */
export const consumeDevToolsParam = () => {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('dev');
  if (raw === null) return false;

  try {
    if (raw === '0' || raw === 'false') {
      localStorage.removeItem(DEV_TOOLS_KEY);
    } else {
      localStorage.setItem(DEV_TOOLS_KEY, 'true');
    }
  } catch (e) {
    /* private mode — the pills just won't appear */
  }

  params.delete('dev');
  const search = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
  );
  return true;
};
